import BaseRenderer from 'diagram-js/lib/draw/BaseRenderer';

import {
  append as svgAppend,
  classes as svgClasses,
  remove as svgRemove
} from 'tiny-svg';

import {
  query as domQuery
} from 'min-dom';

import {
  is
} from 'bpmn-js/lib/util/ModelUtil';

import {
  getDi,
  getSemantic,
  getLabelColor
} from 'bpmn-js/lib/draw/BpmnRenderUtil';

import { isExpanded } from 'bpmn-js/lib/util/DiUtil';

var HIGHER_PRIORITY = 1500;

var DEFAULT_FONT_SIZE = 12;
var DEFAULT_TEXT_ALIGN = 'center';
var LABEL_PADDING = 7;

/**
 * Custom renderer that applies per-element text styling
 * (font size and text alignment) stored as DI attributes.
 *
 * Renders with higher priority than BpmnRenderer (1000).
 * Only handles elements that have custom eliza:fontSize or eliza:textAlign set.
 */
export default function TextStyleRenderer(
    eventBus, bpmnRenderer, textRenderer, config) {

  BaseRenderer.call(this, eventBus, HIGHER_PRIORITY);

  this._bpmnRenderer = bpmnRenderer;
  this._textRenderer = textRenderer;


  var defaultLabelColor = config && config.defaultLabelColor;
  var defaultStrokeColor = config && config.defaultStrokeColor;

  this.canRender = function(element) {
    if (!element.type || element.type === 'label') {
      return false;
    }

    // Only handle Tasks and collapsed SubProcesses with custom text style
    var isTask = is(element, 'bpmn:Task');
    var isCollapsedSubProcess = is(element, 'bpmn:SubProcess') && !isExpanded(element);

    if (!isTask && !isCollapsedSubProcess) {
      return false;
    }

    var di = getDi(element);
    if (!di) {
      return false;
    }

    var fontSize = di.get('eliza:fontSize');
    var textAlign = di.get('eliza:textAlign');

    return fontSize || textAlign;
  };

  this.drawShape = function(parentGfx, element, attrs) {

    // Let default BpmnRenderer draw the shape (box, markers, etc.)
    var shape = bpmnRenderer.drawShape(parentGfx, element, attrs);

    // Remove the default label
    var oldLabel = domQuery('.djs-label', parentGfx);
    if (oldLabel) {
      svgRemove(oldLabel);
    }

    // Read custom text style from DI
    var di = getDi(element);
    var fontSize = di.get('eliza:fontSize') || DEFAULT_FONT_SIZE;
    var textAlign = di.get('eliza:textAlign') || DEFAULT_TEXT_ALIGN;

    // Get element name
    var semantic = getSemantic(element);
    var name = semantic.name;

    if (!name) {
      return shape;
    }

    // Build alignment string for TextRenderer (e.g. 'left-middle', 'center-middle')
    var align = textAlign + '-middle';

    // Create new text element with custom style
    var text = textRenderer.createText(name, {
      align: align,
      box: {
        width: element.width,
        height: element.height
      },
      padding: LABEL_PADDING,
      style: {
        fill: getLabelColor(element, defaultLabelColor, defaultStrokeColor),
        fontSize: fontSize
      }
    });

    svgClasses(text).add('djs-label');
    svgAppend(parentGfx, text);

    return shape;
  };

  this.getShapePath = function(element) {
    return bpmnRenderer.getShapePath(element);
  };
}

TextStyleRenderer.$inject = [
  'eventBus',
  'bpmnRenderer',
  'textRenderer',
  'config.bpmnRenderer'
];
